import type { ReactNode } from 'react';

function renderInline(text: string): ReactNode[] {
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={i} className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-[0.85em]">
          {part.slice(1, -1)}
        </code>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

export default function ReportMarkdown({ content }: { content: string }) {
  const lines = content.split('\n');
  const nodes: ReactNode[] = [];
  let listItems: string[] = [];
  let inCode = false;
  let codeLines: string[] = [];

  const flushList = () => {
    if (!listItems.length) return;
    nodes.push(
      <ol key={`ol-${nodes.length}`} className="list-decimal pl-5 space-y-1 text-sm text-gray-700 dark:text-gray-300 my-2">
        {listItems.map((item, idx) => (
          <li key={idx}>{renderInline(item)}</li>
        ))}
      </ol>,
    );
    listItems = [];
  };

  const flushCode = () => {
    if (!codeLines.length) return;
    nodes.push(
      <pre
        key={`code-${nodes.length}`}
        className="text-xs whitespace-pre-wrap bg-gray-900 text-gray-100 rounded-lg p-3 my-3 overflow-x-auto"
      >
        {codeLines.join('\n')}
      </pre>,
    );
    codeLines = [];
  };

  for (const line of lines) {
    if (line.trim().startsWith('```')) {
      if (inCode) {
        inCode = false;
        flushCode();
      } else {
        flushList();
        inCode = true;
      }
      continue;
    }
    if (inCode) {
      codeLines.push(line);
      continue;
    }
    if (/^\d+\.\s/.test(line.trim())) {
      listItems.push(line.trim().replace(/^\d+\.\s*/, ''));
      continue;
    }
    flushList();
    if (line.startsWith('### ')) {
      nodes.push(
        <h4 key={`h-${nodes.length}`} className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-1 first:mt-0">
          {line.slice(4)}
        </h4>,
      );
    } else if (line.startsWith('## ')) {
      nodes.push(
        <h3 key={`h-${nodes.length}`} className="text-base font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-1 first:mt-0">
          {line.slice(3)}
        </h3>,
      );
    } else if (line.trim()) {
      nodes.push(
        <p key={`p-${nodes.length}`} className="text-sm text-gray-700 dark:text-gray-300 my-1.5 leading-relaxed">
          {renderInline(line)}
        </p>,
      );
    }
  }
  flushList();
  flushCode();

  return <div className="space-y-1">{nodes}</div>;
}
